import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';

const OTP_VALID_SECONDS = 10 * 60; // 10 minutes shown to user

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Locked-account OTP state
  const [isLocked, setIsLocked] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpSuccess, setOtpSuccess] = useState(false);
  const [otpSecondsLeft, setOtpSecondsLeft] = useState(OTP_VALID_SECONDS);
  const timerRef = useRef(null);

  const [searchParams] = useSearchParams();
  const { login, verifyUnlock } = useAuth();
  const navigate = useNavigate();

  const sessionExpired = searchParams.get('expired') === '1';
  const linkExpired    = searchParams.get('linkExpired') === '1';

  // Countdown timer for OTP validity
  useEffect(() => {
    if (isLocked && !otpSuccess) {
      setOtpSecondsLeft(OTP_VALID_SECONDS);
      timerRef.current = setInterval(() => {
        setOtpSecondsLeft((s) => {
          if (s <= 1) {
            clearInterval(timerRef.current);
            return 0;
          }
          return s - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [isLocked, otpSuccess]);

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (!email || !password) {
      setError('Completa todos los campos.');
      setIsLoading(false);
      return;
    }

    const result = await login(email, password);

    if (result.success) {
      navigate(result.user?.is_admin ? '/admin' : '/');
    } else if (result.locked) {
      setIsLocked(true);
    } else {
      setError(result.error || 'Credenciales inválidas.');
    }

    setIsLoading(false);
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setOtpError('');
    if (!otp || otp.length !== 6) {
      setOtpError('Ingresa el código de 6 dígitos.');
      return;
    }
    setOtpLoading(true);
    const result = await verifyUnlock(email, otp);
    setOtpLoading(false);
    if (result.success) {
      clearInterval(timerRef.current);
      setOtpSuccess(true);
      setIsLocked(false);
    } else {
      setOtpError(result.error);
    }
  };

  // ── Locked account / OTP view ────────────────────────────────────────────
  if (isLocked) {
    return (
      <div className="min-h-screen bg-[#0a0e17] flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-[#1a2332] rounded-lg p-8 border border-[#d4af37]/30">
          <h1 className="text-2xl font-bold text-[#d4af37] mb-2">Cuenta bloqueada</h1>
          <p className="text-[#b0b0b0] mb-6">
            Enviamos un código de 6 dígitos a tu correo. Ingrésalo para desbloquear tu cuenta.
          </p>

          {otpError && (
            <Alert className="mb-4 border-red-500 bg-red-500/10">
              <AlertDescription className="text-red-400">{otpError}</AlertDescription>
            </Alert>
          )}

          {otpSecondsLeft > 0 ? (
            <p className="text-sm text-[#b0b0b0] mb-4">
              Código válido por:{' '}
              <span className="text-[#d4af37] font-mono font-bold">{formatTime(otpSecondsLeft)}</span>
            </p>
          ) : (
            <p className="text-sm text-red-400 mb-4">El código expiró. Intenta iniciar sesión de nuevo para recibir uno nuevo.</p>
          )}

          <form onSubmit={handleOtpSubmit} className="space-y-4">
            <div>
              <Label htmlFor="otp" className="text-[#f5f5f0]">
                Código de verificación
              </Label>
              <Input
                id="otp"
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                className="bg-[#0a0e17] border-[#2a3444] text-[#f5f5f0] text-center tracking-[0.5em] text-xl font-mono"
                disabled={otpSecondsLeft === 0}
              />
            </div>
            <Button
              type="submit"
              disabled={otpLoading || otpSecondsLeft === 0}
              className="w-full bg-[#d4af37] text-[#0a0e17] hover:bg-[#e5c158] font-semibold"
            >
              {otpLoading ? 'Verificando…' : 'Desbloquear cuenta'}
            </Button>
          </form>

          <button
            onClick={() => { setIsLocked(false); setOtp(''); setOtpError(''); }}
            className="mt-4 text-sm text-[#b0b0b0] hover:text-[#d4af37] underline block"
          >
            ← Volver al login
          </button>
        </div>
      </div>
    );
  }

  // ── Success after OTP unlock ─────────────────────────────────────────────
  if (otpSuccess) {
    return (
      <div className="min-h-screen bg-[#0a0e17] flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-[#1a2332] rounded-lg p-8 border border-[#2a3444] text-center">
          <h1 className="text-2xl font-bold text-[#d4af37] mb-2">¡Cuenta desbloqueada!</h1>
          <p className="text-[#b0b0b0] mb-6">Ya puedes iniciar sesión con tu contraseña.</p>
          <Button
            onClick={() => setOtpSuccess(false)}
            className="bg-[#d4af37] text-[#0a0e17] hover:bg-[#e5c158] font-semibold"
          >
            Iniciar sesión
          </Button>
        </div>
      </div>
    );
  }

  // ── Main login form ──────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#0a0e17] flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-[#1a2332] rounded-lg p-8 border border-[#2a3444]">
        <h1 className="text-3xl font-bold text-[#f5f5f0] mb-2">Iniciar sesión</h1>
        <p className="text-[#b0b0b0] mb-8">Accede a tu cuenta LXI</p>

        {sessionExpired && !error && (
          <Alert className="mb-6 border-yellow-500 bg-yellow-500/10">
            <AlertDescription className="text-yellow-400">
              Tu sesión ha expirado. Ingresa de nuevo.
            </AlertDescription>
          </Alert>
        )}

        {linkExpired && !error && (
          <Alert className="mb-6 border-[#d4af37]/20 bg-[#d4af37]/5">
            <AlertDescription className="text-[#b0b0b0] text-sm leading-relaxed">
              Tu cuenta está creada y verificada,
              accede con tu contraseña o{' '}
              <Link to="/forgot-password" className="text-[#d4af37] hover:underline">
                restablece tu acceso
              </Link>
              .
            </AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert className="mb-6 border-red-500 bg-red-500/10">
            <AlertDescription className="text-red-400">{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="email" className="text-[#f5f5f0]">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@email.com"
              className="bg-[#0a0e17] border-[#2a3444] text-[#f5f5f0]"
            />
          </div>

          <div>
            <Label htmlFor="password" className="text-[#f5f5f0]">
              Contraseña
            </Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="bg-[#0a0e17] border-[#2a3444] text-[#f5f5f0]"
            />
          </div>

          <div className="text-right">
            <Link to="/forgot-password" className="text-sm text-[#d4af37] hover:underline">
              ¿Olvidaste tu contraseña?
            </Link>
          </div>

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full bg-[#d4af37] text-[#0a0e17] hover:bg-[#e5c158] font-semibold"
          >
            {isLoading ? 'Ingresando…' : 'Iniciar sesión'}
          </Button>
        </form>

        <p className="text-center text-[#b0b0b0] mt-6">
          ¿No tienes cuenta?{' '}
          <Link to="/register" className="text-[#d4af37] hover:underline">
            Regístrate
          </Link>
        </p>
      </div>
    </div>
  );
}
